/**
 * Extracts raw text content from Contentful rich text JSON
 * This is useful for legacy content where markdown syntax is embedded in text nodes
 *
 * @param {Object} contentfulData - Contentful rich text object with format { json: { content: [...] } }
 * @returns {String} - Raw text content with embedded markdown preserved
 */
const extractContentfulText = contentfulData => {
    if (!contentfulData || !contentfulData.json || !contentfulData.json.content) {
        return '';
    }

    // Placeholder to force rendering of empty list items (zero‑width space)
    const EMPTY_LIST_PLACEHOLDER = '\u200B';

    /**
     * Extract text from heading nodes
     */
    const extractHeading = (node, level) => {
        if (node.content) {
            const extractText = n => (n.nodeType === 'text' ? n.value || '' : '');

            return `h${level}. ${node.content.map(extractText).join('')}`;
        }

        return '';
    };

    /**
     * Extract text from list nodes
     */
    const extractList = (node, isOrdered) => {
        if (!node.content) {
            return '';
        }

        return node.content
            .map((item, index) => {
                if (item.nodeType === 'list-item') {
                    const prefix = isOrdered ? `${index + 1}.` : '*';
                    let itemText = '';

                    if (item.content) {
                        itemText = item.content.map(extractText).join('');
                    }

                    // Add a zero-width space placeholder so empty list items still render as distinct lines in downstream wiki/markdown renderers
                    // that otherwise collapse empty bullets. Use it only when an item has no visible text.
                    const hasVisibleText = itemText.replace(/\s+/g, '') !== '';

                    // If no visible text, inject zero-width space so renderer keeps an empty bullet line
                    return hasVisibleText ? `${prefix} ${itemText}` : `${prefix} ${EMPTY_LIST_PLACEHOLDER}`;
                }

                return '';
            })
            .join('\n');
    };

    /**
     * Apply marks (bold, italic, etc.) to text as markdown
     * Handles whitespace properly to ensure markdown renders correctly
     */
    const applyMarks = (text, marks) => {
        if (!marks || marks.length === 0) {
            return text;
        }

        // Extract leading and trailing whitespace
        const leadingSpace = text.match(/^\s*/)[0];
        const trailingSpace = text.match(/\s*$/)[0];
        const trimmedText = text.trim();

        if (!trimmedText) {
            return text; // Return original if only whitespace
        }

        let result = trimmedText;

        // Apply marks to trimmed text only
        marks.forEach(mark => {
            switch (mark.type) {
                case 'bold':
                    result = `*${result}*`;

                    break;
                case 'italic':
                    result = `_${result}_`;

                    break;
                case 'underline':
                    result = `+${result}+`;

                    break;
                case 'code':
                    result = `{{${result}}}`;

                    break;
                case 'superscript':
                    result = `^${result}^`;

                    break;
                case 'subscript':
                    result = `~${result}~`;

                    break;
                default:
                    break;
            }
        });

        // Re-add whitespace around marked text
        return leadingSpace + result + trailingSpace;
    };

    /**
     * Recursively extract text from nodes
     */
    const extractText = node => {
        if (node.nodeType === 'text') {
            const text = node.value || '';

            // Apply marks (bold, italic, etc.) as markdown syntax
            return applyMarks(text, node.marks);
        }

        if (node.nodeType === 'paragraph' && node.content) {
            return node.content.map(extractText).join('');
        }

        if (node.nodeType === 'heading-1') {
            return extractHeading(node, 1);
        }

        if (node.nodeType === 'heading-2') {
            return extractHeading(node, 2);
        }

        if (node.nodeType === 'heading-3') {
            return extractHeading(node, 3);
        }

        if (node.nodeType === 'heading-4') {
            return extractHeading(node, 4);
        }

        if (node.nodeType === 'heading-5') {
            return extractHeading(node, 5);
        }

        if (node.nodeType === 'heading-6') {
            return extractHeading(node, 6);
        }

        if (node.nodeType === 'unordered-list') {
            return extractList(node, false);
        }

        if (node.nodeType === 'ordered-list') {
            return extractList(node, true);
        }

        if (node.nodeType === 'hyperlink' && node.data && node.data.uri) {
            // Convert Contentful hyperlink to markdown link syntax: +[text|url]+
            const linkText = node.content ? node.content.map(extractText).join('') : node.data.uri;
            const url = node.data.uri;

            return `+[${linkText}|${url}]+`;
        }

        if (node.content && Array.isArray(node.content)) {
            return node.content.map(extractText).join('');
        }

        return '';
    };

    // Extract text from all top-level nodes and join with double newlines
    return contentfulData.json.content.map(extractText).join('\n\n');
};

export default extractContentfulText;
